// Piezas compartidas de «Atención Ciudadana» con el lenguaje del SO Orion:
// tokens semánticos, chips de estado con contraste AA en claro y oscuro, KPIs
// con el glifo de tono del shell. Los colores fijos solo pintan datos (mapa,
// barras), nunca texto sobre fondo.

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

// Color por categoría (consistente entre mapa, barras y chips).
// Las 12 categorías oficiales del municipio (backend: CitizenReport.Category).
// Las 2 de licencias son GUÍA — trámites informativos, no incidentes.
export const CATEGORY_META: Record<string, { label: string; color: string }> = {
  // Categorías ACTUALES (backend CitizenReport.Category)
  bache: { label: 'Baches', color: '#f59e0b' },
  alcantarilla: { label: 'Alcantarillas', color: '#d97706' },
  fuga: { label: 'Fugas', color: '#0ea5e9' },
  drenaje: { label: 'Drenaje o colapso', color: '#8b5cf6' },
  falta_agua: { label: 'Falta de agua', color: '#38bdf8' },
  luminaria: { label: 'Luminarias', color: '#eab308' },
  poda: { label: 'Poda de árboles', color: '#22c55e' },
  limpieza_areas_verdes: { label: 'Limpieza áreas verdes', color: '#4ade80' },
  limpieza_areas: { label: 'Limpieza de áreas', color: '#a3e635' },
  recoleccion_basura: { label: 'Recolección de basura', color: '#84cc16' },
  lote_residuos: { label: 'Lotes con residuos', color: '#ca8a04' },
  ruido_quema: { label: 'Ruido y quema', color: '#f97316' },
  bienestar_animal: { label: 'Bienestar animal', color: '#d946ef' },
  semaforo_vial: { label: 'Semáforos', color: '#ef4444' },
  obstruccion_vial: { label: 'Obstrucción vial', color: '#f43f5e' },
  grafiti: { label: 'Grafiti', color: '#a855f7' },
  licencia_construccion: { label: 'Lic. construcción (guía)', color: '#94a3b8' },
  licencia_funcionamiento: { label: 'Lic. funcionamiento (guía)', color: '#94a3b8' },
  otro: { label: 'Otro', color: '#64748b' },
  // Slugs LEGADO (reportes viejos) — mismo color que su equivalente actual.
  alumbrado: { label: 'Alumbrado', color: '#eab308' },
  agua: { label: 'Fuga de agua', color: '#0ea5e9' },
  basura: { label: 'Basura / Limpieza', color: '#84cc16' },
  descacharrizacion: { label: 'Recolección / descacharr.', color: '#14b8a6' },
  vialidad: { label: 'Vialidad / movilidad', color: '#ef4444' },
  podas: { label: 'Podas', color: '#22c55e' },
  infracciones: { label: 'Infracciones', color: '#f43f5e' },
};

export const categoryColor = (c: string) => CATEGORY_META[c]?.color ?? '#64748b';

// Las 5 fases del flujo (orden = kanban). Espejo de CitizenReport.FLOW.
export const FLOW = ['recibido', 'asignado', 'en_proceso', 'atendido', 'cerrado'] as const;

// `color` pinta datos (barras, puntos del mapa); `chip` es texto sobre fondo y
// por eso va con clases que cambian en oscuro (AA en ambos temas).
export const STATUS_META: Record<string, { label: string; color: string; chip: string }> = {
  recibido: { label: 'Recibido', color: '#38bdf8', chip: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  asignado: { label: 'Asignado', color: '#a78bfa', chip: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  en_proceso: { label: 'En proceso', color: '#f59e0b', chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  atendido: { label: 'Atendido', color: '#22c55e', chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  cerrado: { label: 'Cerrado', color: '#64748b', chip: 'bg-muted text-muted-foreground' },
};

export const PRIORITY_META: Record<string, string> = {
  urgente: '#ef4444', alta: '#f59e0b', normal: '#64748b', baja: '#94a3b8',
};

export function StatusChip({ status }: { status: string }) {
  const m = STATUS_META[status] ?? { label: status, chip: 'bg-muted text-muted-foreground' };
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.chip}`}>
      {m.label}
    </span>
  );
}

/** Chip de aviso (revisar, en tu zona, guía…) con icono opcional. */
export function NoteChip({ icon: Icon, tone = 'amber', children, title }: {
  icon?: LucideIcon;
  tone?: 'amber' | 'muted' | 'primary';
  children: ReactNode;
  title?: string;
}) {
  const cls = {
    amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary/10 text-primary',
  }[tone];
  return (
    <span title={title} className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {Icon && <Icon className="h-3 w-3" aria-hidden />}
      {children}
    </span>
  );
}

/** KPI del SO: glifo de tono + etiqueta + cifra + línea de contexto. */
export function Kpi({ icon: Icon, label, value, sub, tone = 'violet' }: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple';
}) {
  return (
    <div className="orion-card min-w-0 rounded-[20px] border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2.5">
        <span className={`orion-tone tone-${tone} grid h-9 w-9 shrink-0 place-items-center rounded-xl`} aria-hidden>
          <Icon size={18} />
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-foreground">{label}</span>
      </div>
      <p className="mt-3 text-[26px] font-semibold leading-none tracking-tight tabular-nums text-foreground sm:text-[30px]">{value}</p>
      {sub && <div className="mt-2 flex min-h-[20px] flex-wrap items-center gap-1.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

/** Filtro tipo píldora (categorías, estados). `dot` = color de la serie. */
export function FilterChip({ active, onClick, dot, children, title }: {
  active: boolean;
  onClick: () => void;
  dot?: string;
  children: ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors ${
        active
          ? 'border-primary/50 bg-primary/10 text-foreground'
          : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'}`}
    >
      {dot && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} aria-hidden />}
      {children}
    </button>
  );
}

/** Interruptor de capa del mapa (histórico, zonas, pines). */
export function LayerToggle({ on, onClick, icon: Icon, children, title }: {
  on: boolean;
  onClick: () => void;
  icon: LucideIcon;
  children: ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      title={title}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition-colors ${
        on ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

/** Encabezado de sección dentro de un panel (detalle del ticket). */
export function SectionLabel({ icon: Icon, children, extra }: {
  icon: LucideIcon;
  children: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
      <Icon className="h-4 w-4 text-primary" />
      {children}
      {extra && <span className="text-xs font-normal text-muted-foreground">{extra}</span>}
    </div>
  );
}

export const fmtN = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('es-MX'));
